'use client';

import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSponsorships } from '@sola/supporter-sdk/hooks/commitments.ts';
import { NameFormats, useSupporterName } from '@sola/supporter-sdk/hooks/details.ts';
export interface GreetingLanguage {
  key: string;
  greeting: string;
  name: string;
  flag: string;
}
const countryToGreetingKey: Record<string, string> = {
  'burkina faso': 'burkinaFaso',
  ethiopia: 'ethiopia',
  ghana: 'ghana',
  kenya: 'kenya',
  rwanda: 'rwanda',
  peru: 'peru',
  bangladesh: 'bangladesh',
  indonesia: 'indonesia',
  philippines: 'philippines',
  haiti: 'haiti'
};
const getGreetingKeyForCountry = (country: string): string | undefined => {
  const normalized = country.trim().toLowerCase();
  return countryToGreetingKey[normalized];
};
const getRandomIndex = (length: number): number => {
  if (length <= 0) {
    return 0;
  }
  return Math.floor(Math.random() * length);
};
const defaultGreeting: GreetingLanguage = {
  key: 'en',
  greeting: 'Hello',
  name: 'English',
  flag: '🇺🇸'
};
const splitGreetingForFlagAnchor = (greeting: string) => {
  return {
    preFlagAnchor: greeting.slice(0, -1),
    flagAnchor: greeting.at(-1) ?? ''
  };
};
export const useHomepageGreeting = () => {
  const {
    t
  } = useTranslation('greetings');
  const supporterNameQuery = useSupporterName(NameFormats.preferred);
  const {
    sponsorships,
    isError: hasSponsorshipsError,
    isLoading: isLoadingSponsorships
  } = useSponsorships();
  const selectedLanguageRef = useRef<GreetingLanguage | null>(null);
  const selectedLanguageCountryKeyRef = useRef<string | undefined>(undefined);
  const languages: GreetingLanguage[] = useMemo(() => {
    const languagesMap = t('languages', {
      returnObjects: true
    }) as Record<string, {
      greeting: string;
      name: string;
      flag: string;
    }>;
    return Object.entries(languagesMap).map(([key, value]) => ({
      key,
      greeting: value.greeting,
      name: value.name,
      flag: value.flag
    }));
  }, [t]);
  const isSupporterNameReady = supporterNameQuery.isSuccess && !supporterNameQuery.isFetching;
  const isGreetingReady = isSupporterNameReady && !isLoadingSponsorships;
  const countryKey = sponsorships.map(s => s.participant?.country?.trim().toLowerCase()).filter((country): country is string => !!country).toSorted().join('|');
  const selectLanguage = () => {
    if (languages.length === 0) {
      return defaultGreeting;
    }
    const matchingKeys = new Set(sponsorships.map(s_0 => s_0.participant?.country).filter((country_0): country_0 is string => !!country_0).map(country_1 => getGreetingKeyForCountry(country_1)).filter((key_0): key_0 is string => !!key_0));
    const matchingLanguages = languages.filter(lang => matchingKeys.has(lang.key));
    if (matchingLanguages.length === 1) {
      return matchingLanguages[0];
    }
    if (matchingLanguages.length > 1) {
      return matchingLanguages[getRandomIndex(matchingLanguages.length)];
    }
    return languages[getRandomIndex(languages.length)] ?? defaultGreeting;
  };
  const shouldSelectGreeting = isGreetingReady && (!selectedLanguageRef.current || selectedLanguageCountryKeyRef.current !== countryKey);
  if (shouldSelectGreeting) {
    // This isn't great. Usually we don't want to mutate refs during render it introduces a side effect.
    // Leaving this here since it's a simple component and this avoids an extra rerender, but remove if a problem.
    selectedLanguageRef.current = selectLanguage();
    selectedLanguageCountryKeyRef.current = countryKey;
  }
  const subtitle = t('subtitle');
  const getFlagTooltip = (languageName: string) => t('flagTooltip', {
    language: languageName
  });
  const selectedLanguage = selectedLanguageRef.current ?? defaultGreeting;
  const splitSelectedGreeting = splitGreetingForFlagAnchor(selectedLanguage.greeting);
  return {
    name: supporterNameQuery.name ?? '',
    selectedLanguage,
    preFlagAnchor: splitSelectedGreeting.preFlagAnchor,
    flagAnchor: splitSelectedGreeting.flagAnchor,
    isLoadingGreeting: !selectedLanguageRef.current,
    hasGreetingError: supporterNameQuery.isError || hasSponsorshipsError,
    languages,
    subtitle,
    getFlagTooltip
  };
};